// src/pages/Upgrades.jsx
import { Fragment, useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { ShieldAlert, X, UserSearch, Target, Send, Sparkles, Cpu, Network, Fingerprint, Share2, Mail, Newspaper, Zap, Key, Building, Globe, Database, MessageSquare, Terminal, ShieldCheck, Lock, Check, Gavel, Scale, ClipboardList, Crown, HelpCircle } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import CheckoutModal from "../components/CheckoutModa"; // fixed typo
import { refreshEntitlements } from "../utils/brandProtection";

const CONTACT_API = "https://whiteintel.io/api/regular/app/v2/contact/contact.php";
const PAYMENT_LINKS_API = "https://whiteintel.io/api/regular/app/v2/user/get_payment_links.php";
const SUBSCRIPTION_API = "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php";

// Map the backend `subscription` value to one of our matrix plan ids.
// Returns null when the user is on a plan that isn't in the matrix
// (e.g. trial / free), so we just don't show a "Your plan" chip.
//
// Known backend codes (from /v2/user/get_subscription.php):
//   "bb-researcher" → researcher
//   "enterprise"    → enterprise
//   "tifirm"        → threat_intel
function normalizeSubscriptionToPlanId(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (!s) return null;
  // Explicit codes first.
  if (s === "tifirm") return "threat_intel";
  if (s === "enterprise") return "enterprise";
  if (s === "bb-researcher") return "researcher";
  // Fuzzy fallback for future / variant codes (e.g. "Threat Intelligence",
  // "researcher_v2", etc.) so a backend rename doesn't silently hide the chip.
  const stripped = s.replace(/[\s_-]+/g, "");
  if (stripped.includes("researcher")) return "researcher";
  if (stripped.includes("threat") && stripped.includes("intel")) return "threat_intel";
  if (stripped.includes("enterprise")) return "enterprise";
  return null;
}

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

export default function Upgrades() {
  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // billing cycle state
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "yearly"

  // Mobile-only: which single plan is currently expanded in the stacked
  // view (the comparison matrix doesn't fit below `md`, so we let users
  // pick one plan and see its full feature list).
  const [mobilePlanId, setMobilePlanId] = useState("threat_intel");

  // checkout modal
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // contact modal
  const [contactOpen, setContactOpen] = useState(false);
  const [forPlan, setForPlan] = useState(null); 

  // payment links fetched from backend
  const [planLinks, setPlanLinks] = useState(null);
  const [linksError, setLinksError] = useState(null);

  // User's current subscription (normalised to a matrix plan id, or null
  // if they're on a plan we don't list — trial / free / etc.).
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // tabs
  const [activeTab, setActiveTab] = useState("licenses"); // "licenses" | "addons"

  // URL <-> tab sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t === "licenses" || t === "addons") setActiveTab(t);
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTab === "addons") params.set("tab", "addons");
    else params.delete("tab");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    );
  }, [activeTab]);

  const openModal = (plan) => {
    setForPlan(plan);
    setContactOpen(true);
  };

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  // fetch payment links
  useEffect(() => {
    let ignore = false;
    async function loadLinks() {
      setLinksError(null);
      try {
        const res = await fetch(PAYMENT_LINKS_API, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }

        const json = await res.json().catch(() => ({}));

        if (!ignore && json?.success && json?.allowed && json?.links) {
          setPlanLinks(json.links);
        } else {
          setPlanLinks(null); 
        }
      } catch (e) {
        if (!ignore) {
          setPlanLinks(null);
          setLinksError("Unable to load payment links right now.");
        }
      }
    }
    loadLinks();
    return () => {
      ignore = true;
    };
  }, []);

  // Fetch the user's current subscription so we can mark "Your plan" on the
  // matching column and disable that column's CTA.
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(SUBSCRIPTION_API, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (!ignore && json?.success) {
          setCurrentPlanId(normalizeSubscriptionToPlanId(json.subscription));
        }
      } catch {
        // Silent — no "Your plan" chip is the right fallback when this fails.
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const threatIntelLinks = planLinks?.threat_intel;
  const threatFeedAddonUrl = threatIntelLinks?.addons?.threat_feed_api_yearly || null;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Upgrades & Licensing</h1>
          </div>
        </div>
      </header>

      {/* Sub-nav — outside the header */}
      <div
        className="border-b border-white/10 bg-[#000414]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-4 md:px-6 flex items-center gap-4 md:gap-5">
          <button
            onClick={() => setActiveTab("licenses")}
            className={`py-3 md:py-2 px-1 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
              activeTab === "licenses"
                ? "text-white border-neutral-200"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Licenses
          </button>
          <span aria-hidden className="hidden md:block h-3.5 w-px bg-neutral-700/60 -mb-px" />
          <button
            onClick={() => setActiveTab("addons")}
            className={`py-3 md:py-2 px-1 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
              activeTab === "addons"
                ? "text-white border-neutral-200"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Add-ons
          </button>
        </div>
      </div>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="upgrades"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pb-12">
          
          {/* Billing Cycle Toggle (Only show on Licenses tab) */}
          {activeTab === "licenses" && (
             <div className="flex justify-center mb-8 mt-4">
              <div className="bg-[#0a1224] p-1 rounded-md border border-sky-900/30 inline-flex relative">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-5 py-1.5 rounded text-[12px] font-medium transition-colors ${
                    billingCycle === "monthly"
                    ? "bg-sky-500/25 text-white border border-sky-500/40"
                    : "text-neutral-300 hover:text-white border border-transparent"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`relative px-5 py-1.5 rounded text-[12px] font-medium transition-colors ${
                    billingCycle === "yearly"
                    ? "bg-sky-500/25 text-white border border-sky-500/40"
                    : "text-neutral-300 hover:text-white border border-transparent"
                  }`}
                >
                  Yearly
                  {/* "Save ~20%" badge — subtle ring chip, no glow. */}
                  <span className={`absolute -top-2.5 -right-7 px-1.5 py-0.5 rounded text-[9.5px] font-medium uppercase tracking-wider transition-colors whitespace-nowrap ${
                    billingCycle === "yearly"
                    ? "bg-sky-500/15 text-sky-200 ring-1 ring-inset ring-sky-500/40"
                    : "bg-sky-900/30 text-sky-300/80 ring-1 ring-inset ring-sky-500/20"
                  }`}>
                    Save ~17%
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Licenses — enterprise comparison matrix (RF / Datadog style).
              Plan columns at top with price + Subscribe; categorized feature
              rows underneath. To re-add Extended Threat Intel, append a 4th
              entry to PLAN_COLUMNS and add an `extended` cell to every row. */}
          {activeTab === "licenses" && (() => {
            const PLAN_COLUMNS = [
              {
                id: "researcher",
                name: "Researcher",
                tagline: "For solo researchers & freelancers",
                icon: Zap,
                priceMonthly: "$200",
                priceYearly: "$2,000 / year",
                yearlySavings: "$400",  // $200 × 12 = $2,400 vs $2,000 yearly
                yearlyOnly: false,
                links: planLinks?.researcher,
                contactName: "Researcher",
                highlight: false,
              },
              {
                id: "enterprise",
                name: "Enterprise",
                tagline: "For SOC teams of 2–5",
                icon: ShieldCheck,
                priceMonthly: "$400",
                priceYearly: "$4,000 / year",
                yearlySavings: "$800",  // $400 × 12 = $4,800 vs $4,000 yearly
                yearlyOnly: false,
                links: planLinks?.enterprise,
                contactName: "Enterprise",
                highlight: false,
              },
              {
                id: "threat_intel",
                name: "Threat Intelligence",
                shortName: "Threat Intel",
                tagline: "For MSSPs & Security products and enterprise SOCs",
                icon: Lock,
                priceMonthly: "$750",
                priceYearly: "$7,500 / year",
                yearlySavings: "$1,500",  // $750 × 12 = $9,000 vs $7,500 yearly
                yearlyOnly: false,
                links: threatIntelLinks,
                contactName: "Threat Intelligence",
                highlight: true,
              },
            ];

            // Cell value vocabulary: "check" → ✓ icon; "dash" or null → —;
            // strings render verbatim. "Add-on" is a string but we tag it
            // visually with a chip via renderCell below.
            const CATEGORIES = [
              {
                name: "Data Access",
                rows: [
                  { label: "Fully unmasked data (incl. passwords)", cells: { researcher: "check", enterprise: "check", threat_intel: "check" } },
                  {
                    label: "Search identifiers",
                    tooltip: "All identifiers: consumer accounts, corporate accounts, email addresses, attack surface, IP addresses, and computer names.",
                    cells: {
                      researcher: "Consumer, corporate, email, attack surface",
                      enterprise: "Consumer, corporate, email, attack surface",
                      threat_intel: "All identifiers available",
                    },
                  },
                  { label: "Data unlocks", cells: { researcher: "750 / month", enterprise: "Unlimited", threat_intel: "Unlimited" } },
                  { label: "User seats", cells: { researcher: "1", enterprise: "2", threat_intel: "3" } },
                ],
              },
              {
                name: "Exports & Downloads",
                rows: [
                  { label: "Data exports", cells: { researcher: "10 / month · 2-month look-back", enterprise: "10 / day · 1-year look-back", threat_intel: "500 / day · 2-year look-back" } },
                  { label: "Full stealer-log archive downloads", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check" } },
                ],
              },
              {
                name: "Monitoring",
                rows: [
                  { label: "Watchlist tokens", cells: { researcher: "10 domains + 5 emails", enterprise: "64 (domains + emails)", threat_intel: "128 (all identifiers)" } },
                  { label: "Dark-web mention monitoring", cells: { researcher: "dash", enterprise: "8 keywords", threat_intel: "16 keywords" } },
                  { label: "Public GitHub repository scanning", cells: { researcher: "dash", enterprise: "8 repositories", threat_intel: "16 repositories" } },
                  { label: "Look-alike domain monitoring", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                  { label: "Direct content access for watchlist events", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                ],
              },
              {
                name: "API Access",
                rows: [
                  { label: "REST API call quota", cells: { researcher: "dash", enterprise: "50 calls / day", threat_intel: "500 calls / day" } },
                  {
                    label: "REST API endpoints",
                    tooltip: "Basic: consumer, corporate, email, and last leaks search endpoints. Full: adds IP-address and computer-name search endpoints.",
                    cells: { researcher: "dash", enterprise: "Basic", threat_intel: "Full" },
                  },
                  { label: "Webhook integration & data streaming", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check" } },
                  { label: "Threat Feed API", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "Add-on" } },
                ],
              },
              {
                name: "Alerting & Integrations",
                rows: [
                  { label: "Real-time alerts", cells: { researcher: "Metadata only", enterprise: "Full · CSV included", threat_intel: "Full · CSV included" } },
                  { label: "Threat Feed access (UI)", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                  { label: "SSO", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                  { label: "Jira", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                  { label: "Slack", cells: { researcher: "dash", enterprise: "check", threat_intel: "check" } },
                  { label: "Investigation+ Module", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "check" } },
                ],
              },
              {
                name: "Commercial & Support",
                rows: [
                  { label: "Resale & customer-facing product rights", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check" } },
                  { label: "Support tier", cells: { researcher: "Standard", enterprise: "Priority", threat_intel: "Priority developer" } },
                ],
              },
            ];

            const renderCell = (v) => {
              if (v === "check") {
                return (
                  <span
                    role="img"
                    aria-label="Included"
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30"
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                );
              }
              if (v === "dash" || v == null) {
                return (
                  <span
                    role="img"
                    aria-label="Not included"
                    className="inline-block h-5 w-5 rounded-full ring-1 ring-inset ring-neutral-600"
                  />
                );
              }
              if (v === "Add-on") {
                return (
                  <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ring-inset ring-amber-500/30 bg-amber-500/10 text-amber-200">
                    <Sparkles className="h-2.5 w-2.5" />
                    Add-on
                  </span>
                );
              }
              return <span className="text-neutral-200 leading-snug">{v}</span>;
            };

            const selectedMobilePlan =
              PLAN_COLUMNS.find((pp) => pp.id === mobilePlanId) || PLAN_COLUMNS[0];

            return (
              <>
              {/* Mobile: chip picker + single-plan stacked view (the matrix is
                  unusable below `md` — 4 columns swipe-horizontally on phones
                  and break comparison context). Fonts unified: labels 13px,
                  descriptions 12px, eyebrows 11px. */}
              <div className="md:hidden space-y-4 max-w-7xl">
                <div className="flex gap-2">
                  {PLAN_COLUMNS.map((pp) => {
                    const isPickedCurrent = currentPlanId === pp.id;
                    const chipLabel = pp.shortName || pp.name;
                    return (
                      <button
                        key={pp.id}
                        onClick={() => setMobilePlanId(pp.id)}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[12.5px] font-medium border transition-colors min-w-0 ${
                          mobilePlanId === pp.id
                            ? "bg-sky-500/20 border-sky-500/40 text-sky-100"
                            : "bg-transparent border-sky-900/40 text-neutral-300 hover:text-white"
                        }`}
                      >
                        <span className="truncate">{chipLabel}</span>
                        {isPickedCurrent && (
                          <span
                            aria-label="Your plan"
                            title="Your plan"
                            className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const p = selectedMobilePlan;
                  const displayCycle = billingCycle;
                  const paymentCycle = p.yearlyOnly ? "yearly" : billingCycle;
                  const activePrice =
                    displayCycle === "monthly"
                      ? p.priceMonthly
                      : (p.priceYearly || "").split(" /")[0];
                  const activePeriod = displayCycle === "monthly" ? "/ mo" : "/ yr";
                  const activeLink =
                    paymentCycle === "monthly" ? p.links?.monthly : p.links?.yearly;
                  const hasLinks = !!(p.links && (p.links.monthly || p.links.yearly));
                  const yearlyTotal = (p.priceYearly || "").split(" /")[0];
                  const isCurrent = currentPlanId === p.id;

                  return (
                    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 overflow-hidden">
                      {/* Summary */}
                      <div className={`px-5 py-5 border-b border-sky-900/30 ${p.highlight ? "bg-sky-500/[0.04]" : ""}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[16px] font-semibold text-neutral-100 tracking-tight">
                            {p.name}
                          </div>
                          {isCurrent && (
                            <span className="text-[9.5px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5">
                              Your plan
                            </span>
                          )}
                          {p.highlight && !isCurrent && (
                            <span className="text-[9.5px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/40 bg-sky-500/10 rounded px-1.5 py-0.5">
                              Most capable
                            </span>
                          )}
                        </div>
                        {p.tagline && (
                          <div className="mt-1 text-[12px] text-neutral-500 leading-snug">
                            {p.tagline}
                          </div>
                        )}
                        <div className="mt-3 flex items-baseline gap-1.5">
                          <span className="text-[24px] font-semibold tabular-nums leading-none text-white">
                            {activePrice}
                          </span>
                          <span className="text-[10.5px] font-medium text-neutral-500 uppercase tracking-wider">
                            {activePeriod}
                          </span>
                        </div>
                        <div className="mt-1.5 text-[12px] text-neutral-500">
                          {p.yearlyOnly
                            ? `Billed annually only${displayCycle === "monthly" && yearlyTotal ? ` · ${yearlyTotal} / yr` : ""}`
                            : displayCycle === "yearly"
                            ? <>Billed annually · <span className="text-emerald-300">{p.yearlySavings} / yr saved</span></>
                            : "Cancel anytime"}
                        </div>
                        <button
                          onClick={() => {
                            if (isCurrent) return;
                            if (hasLinks && activeLink) setCheckoutUrl(activeLink);
                            else openModal(p.contactName);
                          }}
                          disabled={isCurrent}
                          className={`mt-4 w-full py-2.5 rounded-md font-semibold text-[13px] transition-colors ${
                            isCurrent
                              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 cursor-default"
                              : p.highlight
                              ? "bg-sky-500 hover:bg-sky-400 text-white border border-sky-500"
                              : "bg-transparent border border-sky-700/60 hover:bg-white/[0.04] hover:border-sky-500/60 text-neutral-100"
                          }`}
                        >
                          {isCurrent
                            ? "Current plan"
                            : hasLinks && activeLink
                            ? `Subscribe ${paymentCycle === "yearly" ? "yearly" : "monthly"}`
                            : "Contact sales"}
                        </button>
                      </div>

                      {/* Feature list by category — label-left, value-right rows */}
                      <div>
                        {CATEGORIES.map((cat) => (
                          <div key={cat.name}>
                            <div className="px-5 pt-4 pb-2 bg-[#0b1426]/40 border-t border-sky-900/20">
                              <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-300/80">
                                {cat.name}
                              </div>
                            </div>
                            {cat.rows.map((row, ri) => (
                              <div
                                key={ri}
                                className="flex items-start gap-3 px-5 py-2.5 border-t border-sky-900/15"
                              >
                                <div className="text-[13px] text-neutral-300 leading-snug flex-1 min-w-0">
                                  <div>{row.label}</div>
                                  {row.tooltip && (
                                    <div className="mt-0.5 text-[12px] text-neutral-500 leading-snug">
                                      {row.tooltip}
                                    </div>
                                  )}
                                </div>
                                <div className="text-[13px] text-neutral-200 leading-snug text-right max-w-[55%] break-words">
                                  {renderCell(row.cells[p.id])}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Desktop / tablet: full comparison matrix */}
              <div className="hidden md:block max-w-7xl overflow-x-auto rounded-md border border-sky-900/30 bg-[#0a1224]/70">
                <table className="w-full text-[12.5px] border-collapse">
                  {/* Sticky column-header row: plan summary + Subscribe CTA */}
                  <thead className="sticky top-0 z-10 bg-[#0a1224]/95 backdrop-blur">
                    <tr>
                      <th className="w-[26%] min-w-[200px] text-left align-bottom px-5 py-5 border-b border-sky-900/30">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400">
                          Plan comparison
                        </div>
                        <div className="mt-1 text-[11px] text-neutral-500 normal-case tracking-normal font-normal">
                          Prices reflect the {billingCycle === "yearly" ? "annual" : "monthly"} cycle selected above.
                        </div>
                      </th>
                      {PLAN_COLUMNS.map((p) => {
                        const displayCycle = billingCycle;
                        const paymentCycle = p.yearlyOnly ? "yearly" : billingCycle;
                        const activePrice =
                          displayCycle === "monthly"
                            ? p.priceMonthly
                            : (p.priceYearly || "").split(" /")[0];
                        const activePeriod = displayCycle === "monthly" ? "/ mo" : "/ yr";
                        const activeLink =
                          paymentCycle === "monthly" ? p.links?.monthly : p.links?.yearly;
                        const hasLinks = !!(p.links && (p.links.monthly || p.links.yearly));
                        const yearlyTotal = (p.priceYearly || "").split(" /")[0];
                        const isCurrent = currentPlanId === p.id;

                        return (
                          <th
                            key={p.id}
                            className={`min-w-[200px] text-left align-bottom px-5 py-5 border-b border-b-sky-900/30 border-l border-l-sky-900/15 ${
                              p.highlight ? "bg-sky-500/[0.04]" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-[14px] font-semibold text-neutral-100 tracking-tight">
                                {p.name}
                              </div>
                              {isCurrent && (
                                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5">
                                  Your plan
                                </span>
                              )}
                              {p.highlight && !isCurrent && (
                                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/40 bg-sky-500/10 rounded px-1.5 py-0.5">
                                  Most capable
                                </span>
                              )}
                            </div>
                            {/* Audience tagline — answers "who is this plan for?" */}
                            {p.tagline && (
                              <div className="mt-1 text-[11px] text-neutral-500 leading-snug">
                                {p.tagline}
                              </div>
                            )}
                            <div className="mt-3 flex items-baseline gap-1.5">
                              <span className="text-[22px] font-semibold tabular-nums leading-none text-white">
                                {activePrice}
                              </span>
                              <span className="text-[10.5px] font-medium text-neutral-500 uppercase tracking-wider">
                                {activePeriod}
                              </span>
                            </div>
                            <div className="mt-1.5 text-[10px] text-neutral-500">
                              {p.yearlyOnly
                                ? `Billed annually only${displayCycle === "monthly" && yearlyTotal ? ` · ${yearlyTotal} / yr` : ""}`
                                : displayCycle === "yearly"
                                ? <>Billed annually · <span className="text-emerald-300">{p.yearlySavings} / yr saved</span></>
                                : "Cancel anytime"}
                            </div>
                            <button
                              onClick={() => {
                                if (isCurrent) return;
                                if (hasLinks && activeLink) setCheckoutUrl(activeLink);
                                else openModal(p.contactName);
                              }}
                              disabled={isCurrent}
                              className={`mt-3 w-full py-2 rounded-md font-semibold text-[12.5px] transition-colors ${
                                isCurrent
                                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 cursor-default"
                                  : p.highlight
                                  ? "bg-sky-500 hover:bg-sky-400 text-white border border-sky-500"
                                  : "bg-transparent border border-sky-700/60 hover:bg-white/[0.04] hover:border-sky-500/60 text-neutral-100"
                              }`}
                            >
                              {isCurrent
                                ? "Current plan"
                                : hasLinks && activeLink
                                ? `Subscribe ${paymentCycle === "yearly" ? "yearly" : "monthly"}`
                                : "Contact sales"}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  {/* Categorized feature rows */}
                  <tbody>
                    {CATEGORIES.map((cat) => (
                      <Fragment key={cat.name}>
                        <tr>
                          <td
                            colSpan={PLAN_COLUMNS.length + 1}
                            className="px-5 pt-5 pb-2 border-t border-sky-900/20 bg-[#0b1426]/40"
                          >
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-300/80">
                              {cat.name}
                            </div>
                          </td>
                        </tr>
                        {cat.rows.map((row, ri) => (
                          <tr
                            key={`${cat.name}-${ri}`}
                            className="border-t border-sky-900/15 hover:bg-white/[0.015]"
                          >
                            <td className="px-5 py-3 text-neutral-300 align-top w-[26%] min-w-[200px]">
                              <div>{row.label}</div>
                              {row.tooltip && (
                                <div className="mt-0.5 text-[11px] text-neutral-500 leading-snug">
                                  {row.tooltip}
                                </div>
                              )}
                            </td>
                            {PLAN_COLUMNS.map((p) => (
                              <td
                                key={p.id}
                                className={`px-5 py-3 align-top border-l border-sky-900/15 ${
                                  p.highlight ? "bg-sky-500/[0.03]" : ""
                                }`}
                              >
                                {renderCell(row.cells[p.id])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            );
          })()}

          {/* Add-ons */}
          {activeTab === "addons" && (
            <div className="max-w-7xl grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <AddonCard
                name="Threat Feed API"
                description="A comprehensive stream of structured dark-web telemetry providing real-time visibility into darkweb chatters, data & access sales, ransomware lifecycle events, and emerging adversary methodologies."
                quota="50 API calls/day"
                priceNumber={3000}
                priceUnit="USD / Year"
                note="Only available for yearly purchases."
                checkoutUrl={threatFeedAddonUrl}
                onBuy={() => setCheckoutUrl(threatFeedAddonUrl)}
                onContact={() => openModal("Threat Feed API")}
                contactEmail="info@whiteintel.io"
              />

              <AddonCardInvestigations
                onContact={() => openModal("Investigation+ Module")}
              />

              <AddonCardTakedowns
                onContact={() => openModal("Managed Takedowns")}
              />
            </div>
          )}

          {/* Optional: error banner if links failed to load */}
          {linksError && (
            <div className="max-w-7xl mt-6">
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
                {linksError}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Checkout modal (single instance) */}
      <CheckoutModal
        open={!!checkoutUrl}
        checkoutUrl={checkoutUrl}
        onClose={() => {
          setCheckoutUrl(null);
          // The user may have just upgraded — re-check entitlements so the
          // sidebar locks/pills update without waiting for them to refresh.
          refreshEntitlements();
        }}
        title="WhiteIntel — Secure Checkout"
      />

      {/* Contact modal */}
      <ContactLicenseModal
        open={contactOpen}
        planName={forPlan}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
}

/* ---------------- UI Bits ---------------- */

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-2 text-sm rounded-md border transition",
        active
          ? "border-sky-500/40 bg-sky-500/10 text-sky-200"
          : "border-sky-900/30 bg-[#0a1224]/70 text-neutral-300 hover:bg-white/5",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* ---------------- Components ---------------- */

function PlanCard({
  name,
  icon: Icon,
  billingCycle,
  priceMonthly,
  priceYearly,
  features = [],
  availableAddons = [],
  onContact,
  links,
  onOpenCheckout,
  highlight = false,
  // When true, the card ignores the billing-cycle toggle and always shows
  // the yearly price + yearly checkout link. Used for plans only sold
  // annually (e.g. Extended Threat Intel).
  yearlyOnly = false,
}) {
  const hasLinks = !!(links && (links.monthly || links.yearly));

  // Yearly-only plans (e.g. Extended Threat Intel) show a monthly-equivalent
  // price when the toggle is on "monthly" so users can compare against the
  // other plans, but the billing reality + checkout link stay on yearly.
  const displayCycle = billingCycle;
  const paymentCycle = yearlyOnly ? "yearly" : billingCycle;

  const activePrice = displayCycle === "monthly" ? priceMonthly : priceYearly.split(' /')[0];
  const activePeriod = displayCycle === "monthly" ? "/ month" : "/ year";
  const yearlyTotal = (priceYearly || "").split(' /')[0];

  const activeLink = paymentCycle === "monthly" ? links?.monthly : links?.yearly;

  return (
    <div
      className={`relative flex flex-col p-7 rounded-md transition-colors ${
        highlight
          ? "bg-[#0a1224]/80 border border-sky-500/40"
          : "bg-[#0a1224]/70 border border-sky-900/30 hover:border-sky-700/40"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500/15 border border-sky-500/40 text-sky-100 text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-md">
          Most Capable
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div className={`p-2 rounded-md ${highlight ? 'bg-sky-500/15 text-sky-300' : 'bg-white/[0.04] text-neutral-400'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-[15px] font-semibold tracking-tight text-neutral-100">{name}</h3>
      </div>

      <div className="mb-6 border-b border-sky-900/30 pb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[32px] font-semibold tabular-nums leading-none text-white">
            {activePrice}
          </span>
          <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
            {activePeriod}
          </span>
        </div>
        
        <div className="mt-2 space-y-1">
          {yearlyOnly ? (
            <p className="text-[11px] font-medium text-sky-300 uppercase tracking-wider">
              Billed annually only{displayCycle === "monthly" && yearlyTotal ? ` · ${yearlyTotal} / year` : ""}
            </p>
          ) : displayCycle === "yearly" ? (
            <p className="text-[11px] font-medium text-sky-300 uppercase tracking-wider">
              Billed annually
            </p>
          ) : (
            <p className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
              Cancel anytime
            </p>
          )}
          <p className="text-[10px] text-neutral-600 uppercase tracking-wider">
            + Applicable Taxes
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-1 mb-6">
        {features.map((f, i) => {
           const text = typeof f === "string" ? f : f.text;
           const enabled = typeof f === "string" ? true : f.enabled;
           return (
             <div key={i} className="flex items-start py-1">
               {enabled ? (
                 <Check className="h-4 w-4 mt-1 text-sky-500 shrink-0" />
               ) : (
                 <X className="h-4 w-4 mt-1 text-neutral-700 shrink-0" />
               )}
               <span className={`ml-3 text-[13px] leading-tight ${enabled ? "text-neutral-300 font-medium" : "text-neutral-600 line-through decoration-neutral-700"}`}>
                 {text}
               </span>
             </div>
           );
        })}
      </div>

      {availableAddons.length > 0 && (
        <div className="pt-4 mb-6 border-t border-sky-900/30">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400 mb-2">
            Available add-ons
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availableAddons.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ring-1 ring-inset ring-sky-500/20 bg-sky-500/10 text-sky-200"
              >
                <Sparkles className="h-3 w-3" />
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="pt-5 border-t border-sky-900/30 mt-auto">
        {hasLinks && activeLink ? (
           <button
             onClick={() => onOpenCheckout?.(activeLink)}
             className={`w-full py-3 rounded-md font-semibold text-sm transition-colors ${
               highlight
                ? "bg-sky-500/15 border border-sky-500/40 hover:bg-sky-500/25 text-sky-100"
                : "bg-white/5 border border-sky-900/40 hover:bg-white/10 text-neutral-100"
             }`}
           >
             Subscribe {paymentCycle === "yearly" ? "Yearly" : "Monthly"}
           </button>
        ) : (
          <button
            onClick={onContact}
            className="w-full py-3 rounded-md bg-white/5 border border-sky-900/40 text-neutral-200 hover:bg-white/10 font-medium text-sm transition-colors flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Contact Sales Team
          </button>
        )}
      </div>
    </div>
  );
}


function AddonCard({
  name,
  description,
  priceNumber,   
  priceUnit,     
  note,
  checkoutUrl,   
  onBuy,         
  onContact,     
  quota,         
  contactEmail,  
}) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414]`}>
      <div className="space-y-4">
        {/* Header: Title and Pricing */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Terminal className="w-5 h-5 text-sky-400" />
              {name}
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              {description}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">${priceNumber}</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">{priceUnit}</div>
          </div>
        </div>

        {/* Intelligence Vectors (Feature Grid) */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">Intelligence Vectors</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: MessageSquare, label: "Hacker Forum Monitoring" },
              { icon: Globe, label: "Geographical Attribution" },
              { icon: Building, label: "Industry-Specific Targeting" },
              { icon: Zap, label: "Attack Vector Identification" },
              { icon: ShieldAlert, label: "Threat Actor Attribution" },
              { icon: ShieldAlert, label: "Ransomware Event Monitoring" },
              { icon: Database, label: "Exfiltrated Data Auctions" },
              { icon: Key, label: "Initial Access Brokerage" },
              { icon: Terminal, label: "Zero-Day & Exploit Intel" },
              { icon: Newspaper, label: "Mainstream OSINT Signals" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quota & Meta */}
        <div className="flex items-center justify-between gap-3">
          {quota && (
            <div className="px-2 py-0.5 rounded bg-sky-500/5 border border-sky-500/20 text-[10px] font-mono text-sky-400">
              {quota}
            </div>
          )}
          {note && (
            <div className="text-[10px] text-neutral-500 italic text-right">
              {note}
            </div>
          )}
        </div>
      </div>

      {/* Action Footer */}
      <div className="mt-5">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a href={`mailto:${contactEmail}`} className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors">
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function AddonCardInvestigations({
  onBuy,
  onContact,
  checkoutUrl = true,
  contactEmail = "info@whiteintel.io", 
}) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414] relative overflow-hidden`}>
      <div className="absolute -right-16 -top-16 w-64 h-64 bg-sky-500/5 rounded-full blur-3xl group-hover:bg-sky-500/10 transition-colors" />

      <div className="space-y-4 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-400" />
              Investigation+
              <span className="text-[9px] px-1.5 py-0.5 border border-sky-500/30 text-sky-500/80 rounded uppercase tracking-[0.2em] font-medium leading-3">
                Beta
              </span>
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              A dual-purpose neural engine for unmasking threat actor identities and conducting deep-trace forensic analysis on compromised victims.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">$1,500</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">USD / Year</div>
          </div>
        </div>

        {/* Forensic Capabilities Grid */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">Forensic Capabilities</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: UserSearch, label: "Adversary Unmasking" },
              { icon: Target, label: "Detailed Victimology" },
              { icon: ShieldAlert, label: "Automated Risk Scoring" },
              { icon: Zap, label: "Target Prioritization" },
              { icon: Cpu, label: "Neural Identity Mapping" },
              { icon: Network, label: "Lateral Vector Analysis" },
              { icon: Fingerprint, label: "Forensic Footprinting" },
              { icon: Share2, label: "Pivot-Point Discovery" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 relative z-10">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a href={`mailto:${contactEmail}`} className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors">
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function AddonCardTakedowns({ onContact, contactEmail = "info@whiteintel.io" }) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414] relative overflow-hidden`}>
      <div className="absolute -right-16 -top-16 w-64 h-64 bg-violet-500/5 rounded-full blur-3xl group-hover:bg-violet-500/10 transition-colors" />

      <div className="space-y-4 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Gavel className="w-5 h-5 text-sky-400" />
              Managed Takedowns
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              Hand off the registrar abuse process to our analysts. We file,
              follow up, and document evidence on look-alike and phishing
              domains until they go down.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">$1,000</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">
              USD / 10 Takedowns
            </div>
            <div className="text-[9px] text-neutral-500 font-mono mt-0.5">
              $100 each — minimum pack
            </div>
          </div>
        </div>

        {/* Eligibility (single-line) */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/5 border border-amber-500/20 border-dashed">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <p className="text-[11px] text-amber-100/90 leading-tight">
            Enterprise &amp; Threat Intelligence licenses only.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">
            What's Included
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: Target, label: "Look-alike Domains" },
              { icon: ShieldAlert, label: "Phishing &amp; Harvesters" },
              { icon: Send, label: "Registrar Abuse Filing" },
              { icon: ClipboardList, label: "Evidence Bundles" },
              { icon: Scale, label: "Provider Escalations" },
              { icon: ShieldCheck, label: "Lifecycle Tracking" },
              { icon: Sparkles, label: "Ad-hoc Takedowns" },
              { icon: Mail, label: "Analyst Communication" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quota chip */}
        <div className="flex items-center gap-2">
          <div className="px-2 py-0.5 rounded bg-sky-500/5 border border-sky-500/20 text-[10px] font-mono text-sky-400">
            10 takedowns / pack
          </div>
          <div className="text-[10px] text-neutral-500 italic truncate">
            Never expires while subscribed
          </div>
        </div>

        {/* Fairness note */}
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-emerald-500/5 border border-emerald-500/20">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-emerald-100/85 leading-snug">
            <span className="font-semibold">Credit refund policy:</span> if a
            takedown is rejected by a non-ICANN-compliant or hostile registrar
            outside our reach, we restore the credit so you only pay for
            actionable cases.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 relative z-10">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a
              href={`mailto:${contactEmail}`}
              className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors"
            >
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactLicenseModal({ open, onClose, planName }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(null);
  const [err, setErr] = useState(null);
  const dialogRef = useRef(null);

  const [subscriptionType, setSubscriptionType] = useState("");

  const isThreatFeedAddon =
    typeof planName === "string" &&
    planName.toLowerCase().includes("threat feed api");

  const isInvestigations =
    typeof planName === "string" &&
    planName.toLowerCase().includes("investigation");

  const isManagedTakedowns =
    typeof planName === "string" &&
    planName.toLowerCase().includes("takedown");

  const isAddon = isThreatFeedAddon || isInvestigations || isManagedTakedowns;

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    dialogRef.current?.focus();
    return () => prev && prev.focus && prev.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setName("");
      setMsg("");
      setLoading(false);
      setOk(null);
      setErr(null);
      setSubscriptionType("");
    }
  }, [open]);

  if (!open) return null;

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  async function handleSend() {
    setOk(null);
    setErr(null);
    if (!msg.trim()) {
      setErr("Please enter a message.");
      return;
    }

    const category = isAddon ? "addon_inquiry" : "subscription_upgrade";
    const title = isAddon
      ? `${planName || "General"} Inquiry`
      : `${planName || "General"} License Inquiry (${
          subscriptionType === "yearly" ? "Yearly" : "Monthly"
        })`;

    const payload = {
      category,
      title,
      message: name.trim() ? `From: ${name}\n\n${msg}` : msg,
    };

    try {
      const res = await fetch(CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson?.error) errMsg = errJson.error;
        } catch {}
        throw new Error(errMsg);
      }

      const json = await res.json();
      if (json && (json.success === true || json.status === "ok")) {
        setOk("Thanks! Your message has been sent. We’ll get back to you shortly.");
      } else {
        setOk(
          "You will receive an email from us shortly. If it does not appear in your inbox, please check your spam or junk folder."
        );
      }
      setName("");
      setMsg("");
    } catch (e) {
      setErr(e.message || "Failed to send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[420]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#000414] border-l border-neutral-800 shadow-[0_0_20px_rgba(56,189,248,0.08)] outline-none flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <div>
            <div className="text-sm text-neutral-400">{planName ? `Contact — ${planName}` : "Contact"}</div>
            <div className="text-xl font-semibold">
              Contact Us{planName ? ` for ${planName}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" title="Close">
            <X className="h-5 w-5 text-neutral-300" />
          </button>
        </div>

        {/* Banners */}
        {err && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex items-start justify-between">
              <span className="pr-4">{err}</span>
              <button onClick={() => setErr(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}
        {ok && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
              <span className="pr-4">{ok}</span>
              <button onClick={() => setOk(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-6 overflow-y-auto flex-1">
          <div className={`rounded-md ${frosted} p-4 space-y-4`}>
            <div className="grid grid-cols-1 gap-4">
              {/* Name */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Your Name</div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jane Doe"
                  className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>

              {/* Subscription Type — HIDDEN for Addons */}
              {!isAddon && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Subscription Type</div>
                  <select
                    value={subscriptionType}
                    onChange={(e) => setSubscriptionType(e.target.value)}
                    className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                  >
                    <option value="">Select subscription type</option>
                    <option value="monthly">Monthly Subscription</option>
                    <option value="yearly">Yearly Subscription</option>
                  </select>
                </div>
              )}

              {/* Message */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Message</div>
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder={
                    isThreatFeedAddon
                      ? "Tell us about your needs for the Threat Feed API…"
                      : `Tell us about your needs for ${planName || "the selected option"}…`
                  }
                  rows={6}
                  className="w-full rounded-md border border-sky-900/30 bg-[#0a1224]/80 p-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>
            </div>

            {/* KYC Banner — HIDDEN for Addons */}
            {!isAddon && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">KYC & Due Diligence</div>
                    <p className="mt-1 text-sm opacity-90">
                      Because our threat intelligence may include sensitive or confidential information, we conduct Know Your
                      Customer (KYC) checks before enabling full access. To accelerate verification, please include as much context
                      as possible:
                    </p>
                    <ul className="mt-2 text-sm list-disc pl-5 space-y-1">
                      <li>Intended use cases</li>
                      <li>LinkedIn profile or team page</li>
                      <li>Company website and corporate email domain</li>
                      <li>Organization name, role, and business justification for access</li>
                      <li>Bug bounty platform profiles for researchers (HackerOne, Bugcrowd, etc.)</li>
                      <li>Personal website / portfolio</li>
                    </ul>
                    <p className="mt-2 text-xs opacity-80">
                      Submissions are reviewed by our compliance team. Incomplete requests may delay activation.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 flex items-center justify-end gap-3 border-t border-neutral-800">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border border-neutral-700 text-neutral-200 hover:bg-white/5"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}